import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useRestApi } from "@/context/api";
import { useCurrentCompany } from "@/context/auth";
import type { TeamDto } from "@/types/auth";

import { queryKeys } from "./queryKeys";

export const useTeamsQuery = () => {
  const { getTeams } = useRestApi();
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.TEAMS, currentCompany.id],
    queryFn: () => getTeams(currentCompany.id),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/** Puts a team under another parent, changing nothing else: Move team and its Undo. */
export const useMoveTeamMutation = () => {
  const { moveTeam } = useRestApi();
  const queryClient = useQueryClient();
  const currentCompany = useCurrentCompany();

  return useMutation({
    mutationFn: ({ teamId, parentTeamId }: { teamId: string; parentTeamId: string }) =>
      moveTeam(teamId, parentTeamId, currentCompany.id),
    // On the mutation, not on a mutate call: Undo runs from a toast after the panel may be gone
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.TEAMS, currentCompany.id] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.TEAM_TREE] });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.TEAM_TREE_DETAILS, currentCompany.id, variables.teamId],
      });
    },
  });
};

export const useUpdateTeamMutation = () => {
  const { updateTeam } = useRestApi();
  const queryClient = useQueryClient();
  const currentCompany = useCurrentCompany();

  return useMutation({
    mutationFn: ({
      teamId,
      dto,
    }: {
      teamId: string;
      dto: Partial<TeamDto> & Pick<TeamDto, "teamId" | "name">;
    }) => updateTeam(teamId, dto as TeamDto, currentCompany.id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.TEAMS, currentCompany.id] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.TEAM_TREE] });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.TEAM_TREE_DETAILS, currentCompany.id, variables.teamId],
      });
    },
  });
};
